import React, { useMemo, useState } from 'react';
import './css/index.scss';
import PageTitle from '../../components/PageTitle';
import MyButton from '../../components/MyButton';
import SearchInput from '../../components/SearchInput';
import sendRequest from '../../helpers/sendRequest';
import Api from '../../Api';
import MyModal from '../../components/MyModal';
import { INTERFACE_LANGUAGES } from '../../helpers/interfaceLanguages';

function emptyTranslations() {
  return INTERFACE_LANGUAGES.reduce((acc, lang) => {
    acc[lang.code] = { title: '', text: '' };
    return acc;
  }, {});
}

function Notifications() {
  const [translations, setTranslations] = useState(emptyTranslations);
  const [activeLang, setActiveLang] = useState(INTERFACE_LANGUAGES[0].code);
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalText, setModalText] = useState('');
  const [modalState, setModalState] = useState('');

  const missingLanguages = useMemo(() => INTERFACE_LANGUAGES.filter((lang) => {
    const entry = translations[lang.code] || {};
    return !entry.title?.trim() || entry.title.trim().length < 2
      || !entry.text?.trim() || entry.text.trim().length < 2;
  }), [translations]);

  const filledCount = INTERFACE_LANGUAGES.length - missingLanguages.length;

  const updateField = (field, value) => {
    setTranslations((prev) => ({
      ...prev,
      [activeLang]: {
        ...(prev[activeLang] || { title: '', text: '' }),
        [field]: value,
      },
    }));
    setFieldErrors((prev) => ({ ...prev, [activeLang]: '' }));
  };

  const validateForm = () => {
    const nextErrors = {};
    INTERFACE_LANGUAGES.forEach((lang) => {
      const entry = translations[lang.code] || {};
      if (!entry.title?.trim() || entry.title.trim().length < 2
        || !entry.text?.trim() || entry.text.trim().length < 2) {
        nextErrors[lang.code] = 'Заполните заголовок и текст (мин. 2 символа)';
      }
    });
    setFieldErrors(nextErrors);
    const firstMissing = INTERFACE_LANGUAGES.find((lang) => nextErrors[lang.code]);
    if (firstMissing) setActiveLang(firstMissing.code);
    return Object.keys(nextErrors).length === 0;
  };

  const onSendNotification = async () => {
    if (!validateForm()) {
      return;
    }

    const payloadTranslations = INTERFACE_LANGUAGES.reduce((acc, lang) => {
      acc[lang.code] = {
        title: translations[lang.code].title.trim(),
        text: translations[lang.code].text.trim(),
      };
      return acc;
    }, {});

    const data = await sendRequest({
      request: Api.sendGlobalNotification,
      payload: { translations: payloadTranslations },
      warnErrorText: 'while sending global notification',
      setLoading,
    });

    setIsModalVisible(true);
    if (data?.status !== 'ok') {
      const missing = Array.isArray(data?.missing_languages)
        ? `\nНе хватает языков: ${data.missing_languages.join(', ')}`
        : '';
      const errorText = data?.message || 'Не удалось установить соединение с сервером.';
      setModalText(`Возникла ошибка:\n\n${errorText}${missing}`);
      setModalState('error');
    } else {
      const stats = data.statistics || {};
      const sent = stats.sent || 0;
      const skipped = stats.skipped || 0;
      const total = stats.total || 0;
      const pushSuccess = stats.push?.success || 0;
      const pushFailed = stats.push?.failed || 0;

      let statisticsText = 'Уведомление успешно отправлено!\n\n';
      statisticsText += 'Статистика отправки:\n\n';
      statisticsText += `Всего устройств: ${total}\n`;
      statisticsText += `Отправлено: ${sent}\n`;
      if (skipped > 0) {
        statisticsText += `Пропущено (уведомления отключены): ${skipped}\n`;
      }
      if (pushSuccess > 0 || pushFailed > 0) {
        statisticsText += '\nPush-уведомления:\n';
        statisticsText += `Успешно: ${pushSuccess}\n`;
        if (pushFailed > 0) {
          statisticsText += `Неудачно: ${pushFailed}\n`;
        }
      }

      setModalText(statisticsText);
      setModalState('success');
    }
  };

  const onCloseModal = () => {
    setIsModalVisible(false);
    setTimeout(() => {
      setModalText('');
      setModalState('');
    }, 400);

    if (modalState === 'success') {
      setTranslations(emptyTranslations());
      setFieldErrors({});
      setActiveLang(INTERFACE_LANGUAGES[0].code);
    }
  };

  const activeEntry = translations[activeLang] || { title: '', text: '' };

  return (
    <div className="notifications">
      <PageTitle text="Уведомления (во всех странах)" />
      <div className="notifications_container">
        <div>
          <p className="notifications_warning">
            ВАЖНО! Уведомление уходит всем пользователям, у которых включены push.
            Текст обязателен на всех 10 языках интерфейса — каждому устройству уйдёт
            версия на его языке.
          </p>

          <p className="notifications_progress">
            Заполнено языков:
            {' '}
            {filledCount}
            /
            {INTERFACE_LANGUAGES.length}
          </p>

          <div className="notifications_langs">
            {INTERFACE_LANGUAGES.map((lang) => {
              const entry = translations[lang.code] || {};
              const filled = entry.title?.trim()?.length >= 2 && entry.text?.trim()?.length >= 2;
              const hasError = Boolean(fieldErrors[lang.code]);
              return (
                <button
                  key={lang.code}
                  type="button"
                  className={[
                    'notifications_lang',
                    activeLang === lang.code ? 'active' : '',
                    filled ? 'filled' : '',
                    hasError ? 'error' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => setActiveLang(lang.code)}
                >
                  {lang.code}
                </button>
              );
            })}
          </div>

          <p className="notifications_active_label">
            {INTERFACE_LANGUAGES.find((lang) => lang.code === activeLang)?.label}
            {' '}
            (
            {activeLang}
            )
          </p>

          <SearchInput
            wrapperClassName="notifications_input"
            placeholder="Заголовок"
            withLeftIcon={false}
            value={activeEntry.title}
            onChange={(value) => updateField('title', value)}
            error={fieldErrors[activeLang]}
            shouldValueConsiderAsFocus={false}
          />
          <SearchInput
            wrapperClassName="notifications_input notifications_input_large"
            placeholder="Текст уведомления"
            withLeftIcon={false}
            value={activeEntry.text}
            onChange={(value) => updateField('text', value)}
            isLarge
            shouldValueConsiderAsFocus={false}
          />

          {missingLanguages.length > 0 && (
            <p className="notifications_missing">
              Не заполнены:
              {' '}
              {missingLanguages.map((lang) => lang.label).join(', ')}
            </p>
          )}

          <MyButton
            text="Отправить уведомление"
            onClick={onSendNotification}
            loading={loading}
            className="notifications_button"
            disabled={missingLanguages.length > 0}
          />
        </div>
      </div>

      <MyModal isOpen={isModalVisible} closeFunction={onCloseModal}>
        <div style={{
          padding: '20px', alignItems: 'center', display: 'flex', flexDirection: 'column',
        }}
        >
          <p style={{ whiteSpace: 'pre-wrap', marginBottom: '40px' }}>{modalText}</p>
          <MyButton
            text={modalState === 'error' ? 'Закрыть' : 'ОК'}
            onClick={onCloseModal}
          />
        </div>
      </MyModal>
    </div>
  );
}

export default Notifications;
